/**
 * CR283: Merchant Directory Page
 * CR314: Added category icon fallback toggle
 * CR413: Added + Add Merchant button with CreateMerchantDialog
 * CR550: Added region filter (behind ff_regions_filter)
 * CR: Split Active tab into Active Scan + Active No Scan
 * Route: /admin/merchants/directory
 * Tabs: Active Scan | Active No Scan | Pending | Hidden | Duplicates | Deleted
 */

import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import {
  useMerchantsDirectory,
  useMerchantsDuplicates,
  useMerchantsPendingCount,
  type MerchantDirectoryRow,
} from "@/hooks/useMerchantsDirectory";
import { MerchantDirectoryTable } from "@/components/admin/merchant-directory/MerchantDirectoryTable";
import { MerchantDuplicatesTable } from "@/components/admin/merchant-directory/MerchantDuplicatesTable";
import { CreateMerchantDialog } from "@/components/admin/merchant-directory/CreateMerchantDialog";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { FLAGS } from "@/lib/featureFlags";
import { useRegions, useRegionMerchantKeys } from "@/hooks/useRegions";
import { RegionFilterDropdown } from "@/components/admin/merchant-directory/RegionFilterDropdown";

/** localStorage key for category icon fallback preference */
const CATEGORY_FALLBACK_KEY = "merchantDirectory.categoryIconFallbackEnabled";

/** Sort merchants alphabetically by display_name (case-insensitive), then by merchant_key */
function sortMerchantsAlphabetically(merchants: MerchantDirectoryRow[]): MerchantDirectoryRow[] {
  return [...merchants].sort((a, b) => {
    const nameCompare = a.display_name.localeCompare(b.display_name, undefined, { sensitivity: "base" });
    if (nameCompare !== 0) return nameCompare;
    return a.merchant_key.localeCompare(b.merchant_key);
  });
}

/** CR550: Filter merchants by region membership (client-side) */
function filterByRegion(
  merchants: MerchantDirectoryRow[],
  regionMerchantKeys: Set<string> | undefined
): MerchantDirectoryRow[] {
  if (!regionMerchantKeys) return merchants;
  return merchants.filter((m) => regionMerchantKeys.has(m.merchant_key));
}

type MerchantDirectoryTab = "active_scan" | "active_noscan" | "pending" | "hidden" | "duplicates" | "deleted";

function parseTab(value: string | null): MerchantDirectoryTab {
  if (
    value === "active_scan" ||
    value === "active_noscan" ||
    value === "pending" ||
    value === "hidden" ||
    value === "duplicates" ||
    value === "deleted"
  ) {
    return value;
  }
  return "active_scan";
}

export default function MerchantDirectory() {
  const { isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<MerchantDirectoryTab>(() => parseTab(searchParams.get("tab")));
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  useEffect(() => {
    const fromUrl = parseTab(searchParams.get("tab"));
    if (fromUrl !== activeTab) {
      setActiveTab(fromUrl);
    }
    if (!searchParams.get("tab")) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set("tab", "active_scan");
      setSearchParams(nextParams, { replace: true });
    }
  }, [searchParams, setSearchParams, activeTab]);

  // CR314: Category icon fallback toggle (persisted in localStorage)
  const [categoryIconFallbackEnabled, setCategoryIconFallbackEnabled] = useState(() => {
    const stored = localStorage.getItem(CATEGORY_FALLBACK_KEY);
    return stored === null ? true : stored === "true"; // Default: ON
  });

  const handleToggleCategoryIconFallback = (enabled: boolean) => {
    setCategoryIconFallbackEnabled(enabled);
    localStorage.setItem(CATEGORY_FALLBACK_KEY, String(enabled));
  };

  // CR550: Region filter (behind feature flag)
  const ffRegionsFilter = useFeatureFlag(FLAGS.REGIONS_FILTER);
  const { data: regions = [] } = useRegions();
  const [selectedRegionId, setSelectedRegionId] = useState<string | null>(null);
  const { data: regionMerchantKeys, isLoading: loadingRegionKeys } = useRegionMerchantKeys(
    ffRegionsFilter ? selectedRegionId : null
  );

  const { data: pendingCount = 0 } = useMerchantsPendingCount();
  const { data: pendingMerchants = [], isLoading: loadingPending } = useMerchantsDirectory("pending");
  const { data: activeScanMerchants = [], isLoading: loadingActiveScan } = useMerchantsDirectory("active", { scanEnabled: true });
  const { data: activeNoScanMerchants = [], isLoading: loadingActiveNoScan } = useMerchantsDirectory("active", { scanEnabled: false });
  const { data: hiddenMerchants = [], isLoading: loadingHidden } = useMerchantsDirectory("hidden");
  const { data: deletedMerchants = [], isLoading: loadingDeleted } = useMerchantsDirectory("deleted");
  const { data: duplicates = [], isLoading: loadingDuplicates } = useMerchantsDuplicates();

  // Apply alphabetical sorting, then region filter
  const sortedActiveScanMerchants = useMemo(
    () => filterByRegion(sortMerchantsAlphabetically(activeScanMerchants), ffRegionsFilter ? regionMerchantKeys : undefined),
    [activeScanMerchants, regionMerchantKeys, ffRegionsFilter]
  );
  const sortedActiveNoScanMerchants = useMemo(
    () => filterByRegion(sortMerchantsAlphabetically(activeNoScanMerchants), ffRegionsFilter ? regionMerchantKeys : undefined),
    [activeNoScanMerchants, regionMerchantKeys, ffRegionsFilter]
  );
  const sortedPendingMerchants = useMemo(
    () => filterByRegion(sortMerchantsAlphabetically(pendingMerchants), ffRegionsFilter ? regionMerchantKeys : undefined),
    [pendingMerchants, regionMerchantKeys, ffRegionsFilter]
  );
  const filteredHiddenMerchants = useMemo(
    () => filterByRegion(hiddenMerchants, ffRegionsFilter ? regionMerchantKeys : undefined),
    [hiddenMerchants, regionMerchantKeys, ffRegionsFilter]
  );
  const sortedDeletedMerchants = useMemo(
    () => filterByRegion(sortMerchantsAlphabetically(deletedMerchants), ffRegionsFilter ? regionMerchantKeys : undefined),
    [deletedMerchants, regionMerchantKeys, ffRegionsFilter]
  );

  if (!isAdmin) {
    return (
      <AdminLayout>
        <div className="text-center py-12 text-muted-foreground">
          Admin access required
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold">Merchant Directory</h1>
            <p className="text-muted-foreground">
              {pendingCount > 0
                ? `${pendingCount} merchants pending review. `
                : ""}
              Manage all merchants: logos, categories, types, and aliases.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* CR550: Region filter dropdown */}
            {ffRegionsFilter && (
              <RegionFilterDropdown
                regions={regions}
                selectedRegionId={selectedRegionId}
                onRegionChange={setSelectedRegionId}
                loading={loadingRegionKeys}
              />
            )}
            {/* CR413: Add Merchant button */}
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Merchant
            </Button>
          </div>
        </div>

        {/* CR413: Create Merchant Dialog */}
        <CreateMerchantDialog
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
        />

        {/* Tabs: Active Scan | Active No Scan | Pending | Hidden | Duplicates | Deleted */}
        <Tabs
          value={activeTab}
          onValueChange={(v) => {
            const next = parseTab(v);
            setActiveTab(next);
            const nextParams = new URLSearchParams(searchParams);
            nextParams.set("tab", next);
            setSearchParams(nextParams, { replace: true });
          }}
        >
          <TabsList>
            <TabsTrigger value="active_scan" className="gap-2">
              Active Scan
              <Badge variant="secondary" className="ml-1">
                {sortedActiveScanMerchants.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="active_noscan" className="gap-2">
              Active No Scan
              <Badge variant="secondary" className="ml-1">
                {sortedActiveNoScanMerchants.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="pending" className="gap-2">
              Pending
              {sortedPendingMerchants.length > 0 && (
                <Badge variant="secondary" className="ml-1">
                  {sortedPendingMerchants.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="hidden" className="gap-2">
              Hidden
              <Badge variant="secondary" className="ml-1">
                {filteredHiddenMerchants.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="duplicates" className="gap-2">
              Duplicates
              <Badge variant="secondary" className="ml-1">
                {duplicates.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="deleted" className="gap-2">
              Deleted
              <Badge variant="secondary" className="ml-1">
                {sortedDeletedMerchants.length}
              </Badge>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="active_scan" className="mt-4">
            <MerchantDirectoryTable
              merchants={sortedActiveScanMerchants}
              loading={loadingActiveScan}
              status="active_scan"
              categoryIconFallbackEnabled={categoryIconFallbackEnabled}
              onToggleCategoryIconFallback={handleToggleCategoryIconFallback}
            />
          </TabsContent>

          <TabsContent value="active_noscan" className="mt-4">
            <MerchantDirectoryTable
              merchants={sortedActiveNoScanMerchants}
              loading={loadingActiveNoScan}
              status="active_noscan"
              categoryIconFallbackEnabled={categoryIconFallbackEnabled}
              onToggleCategoryIconFallback={handleToggleCategoryIconFallback}
            />
          </TabsContent>

          <TabsContent value="pending" className="mt-4">
            <MerchantDirectoryTable
              merchants={sortedPendingMerchants}
              loading={loadingPending}
              status="pending"
              categoryIconFallbackEnabled={categoryIconFallbackEnabled}
              onToggleCategoryIconFallback={handleToggleCategoryIconFallback}
            />
          </TabsContent>

          <TabsContent value="hidden" className="mt-4">
            <MerchantDirectoryTable
              merchants={filteredHiddenMerchants}
              loading={loadingHidden}
              status="hidden"
              categoryIconFallbackEnabled={categoryIconFallbackEnabled}
              onToggleCategoryIconFallback={handleToggleCategoryIconFallback}
            />
          </TabsContent>

          <TabsContent value="duplicates" className="mt-4">
            <MerchantDuplicatesTable
              duplicates={duplicates}
              loading={loadingDuplicates}
            />
          </TabsContent>

          <TabsContent value="deleted" className="mt-4">
            <MerchantDirectoryTable
              merchants={sortedDeletedMerchants}
              loading={loadingDeleted}
              status="deleted"
              categoryIconFallbackEnabled={categoryIconFallbackEnabled}
              onToggleCategoryIconFallback={handleToggleCategoryIconFallback}
            />
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
